import { useEffect, useState } from "react"
import { apiClient } from "@/services/api.client"

export type PlanCode = "plus" | "pro"

/** quantity null = unlimited (commerce.plan_allowances.quantity is nullable). */
export interface PlanAllowance { metric: string; quantity: number | null; window: string }
export interface Plan {
  plan: string
  price_minor: number
  currency: string
  billing_period: string
  gst_inclusive: boolean
  allowances: PlanAllowance[]
  /** The largest upload this plan takes, if the API says. */
  max_file_bytes?: number | null
}

/** An add-on for sale (Pro only): GET /billing/addons. */
export interface Addon { sku: string; metric: string; quantity: number; price_minor: number }

/** What the pages say when the plan list has not loaded (or the request failed). */
export const FALLBACK_PRICE_MINOR: Record<PlanCode, number> = { plus: 19900, pro: 199900 }
export const FALLBACK_ADDON_MINOR: Record<string, number> = { extra_draft: 79900, extra_edits: 29900 }
export const FALLBACK_ADDONS: Addon[] = [
  { sku: "extra_draft", metric: "order.draft", quantity: 1, price_minor: 79900 },
  { sku: "extra_edits", metric: "order.draft_edit", quantity: 5, price_minor: 29900 },
]

const STALE_MS = 10 * 60 * 1000
let plansCache: { at: number; plans: Plan[] } | null = null
let plansInflight: Promise<Plan[]> | null = null
let addonsCache: { at: number; addons: Addon[] } | null = null
let addonsInflight: Promise<Addon[]> | null = null

const list = <T,>(body: unknown, key: string): T[] => {
  if (Array.isArray(body)) return body as T[]
  const inner = (body as Record<string, unknown> | null)?.[key]
  return Array.isArray(inner) ? (inner as T[]) : []
}

/** GET /billing/plans (plus and pro), cached for 10 minutes. */
export function fetchPlans(force = false): Promise<Plan[]> {
  if (!force && plansCache && Date.now() - plansCache.at < STALE_MS) return Promise.resolve(plansCache.plans)
  plansInflight ??= apiClient
    .get<unknown>("/api/v1/billing/plans")
    .then((body) => {
      const plans = list<Record<string, unknown>>(body, "plans").map((p) => ({
        ...p, plan: String(p.plan ?? p.code ?? ""), allowances: Array.isArray(p.allowances) ? p.allowances : [],
      }) as unknown as Plan)
      plansCache = { at: Date.now(), plans }
      return plans
    })
    .finally(() => { plansInflight = null })
  return plansInflight
}

/** GET /billing/addons (the active SKUs), cached for 10 minutes. */
export function fetchAddons(force = false): Promise<Addon[]> {
  if (!force && addonsCache && Date.now() - addonsCache.at < STALE_MS) return Promise.resolve(addonsCache.addons)
  addonsInflight ??= apiClient
    .get<unknown>("/api/v1/billing/addons")
    .then((body) => {
      const addons = list<Record<string, unknown>>(body, "addons").map((a) => ({
        ...a, sku: String(a.sku ?? a.code ?? ""),
      }) as unknown as Addon)
      addonsCache = { at: Date.now(), addons }
      return addons
    })
    .finally(() => { addonsInflight = null })
  return addonsInflight
}

export const resetPlanCache = (): void => { plansCache = null; plansInflight = null; addonsCache = null; addonsInflight = null }

/** The purchasable plans, [] while loading or when the request fails (callers fall back to copy). */
export function usePlans(enabled = true): Plan[] {
  const [plans, setPlans] = useState<Plan[]>(plansCache?.plans ?? [])
  useEffect(() => {
    if (!enabled) return
    let live = true
    fetchPlans().then((p) => { if (live) setPlans(p) }).catch(() => undefined)
    return () => { live = false }
  }, [enabled])
  return plans
}

/** The active add-on SKUs, [] while loading or on failure. */
export function useAddons(enabled = true): Addon[] {
  const [addons, setAddons] = useState<Addon[]>(addonsCache?.addons ?? [])
  useEffect(() => {
    if (!enabled) return
    let live = true
    fetchAddons().then((a) => { if (live) setAddons(a) }).catch(() => undefined)
    return () => { live = false }
  }, [enabled])
  return addons
}

export const planOf = (plans: Plan[], code: PlanCode): Plan | null => plans.find((p) => p.plan === code) ?? null

/** One plan by code (the Pro plan by default). */
export function usePlan(enabled = true, code: PlanCode = "pro"): Plan | null {
  return planOf(usePlans(enabled), code)
}

/** The add-on selling a metric, from the loaded list or the shipped defaults. */
export function addonFor(addons: Addon[], metric: string, sku?: string | null): Addon | null {
  const all = addons.length ? addons : FALLBACK_ADDONS
  return all.find((a) => (sku ? a.sku === sku : a.metric === metric)) ?? null
}

/** "₹1,999" for paise. */
export function rupees(minor: number): string {
  const r = minor / 100
  return `₹${r.toLocaleString("en-IN", { maximumFractionDigits: Number.isInteger(r) ? 0 : 2, minimumFractionDigits: Number.isInteger(r) ? 0 : 2 })}`
}

/** The plan's price in paise, or the shipped default. */
export const priceMinor = (plan: Plan | null, code: PlanCode): number =>
  plan && plan.currency === "INR" && plan.price_minor > 0 ? plan.price_minor : FALLBACK_PRICE_MINOR[code]

/** "₹1,999/month (incl. GST)" from price_minor. */
export function priceCopy(plan: Plan | null, code: PlanCode = "pro"): string {
  const gst = plan ? plan.gst_inclusive : true
  return `${rupees(priceMinor(plan, code))}/month${gst ? " (incl. GST)" : ""}`
}
